/**
 * EE: the 3 tasks, typed like at the centre. Live word counter against the limits of the format,
 * automatic saving while typing, an accent bar that inserts at the cursor, submit per task.
 */

import { ApiError, academyAttemptsNextCreate, academyAttemptsTextCreate } from "@agencyos/api-client";
import { Button } from "@agencyos/ui-web";
import { ArrowUpFromLine, Check, Keyboard } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ACCENTS, QUOTES, countWords, insertAt, wordStatus } from "./text";
import { type PlayerState, type StateTask, localized } from "./types";

type SaveState = "idle" | "saving" | "saved" | "error";

function AccentBar({ onInsert }: { onInsert: (piece: string) => void }) {
  const { t } = useTranslation();
  const [upper, setUpper] = useState(false);
  const keys = [...ACCENTS.map((c) => (upper ? c.toUpperCase() : c)), ...QUOTES];
  return (
    <div className="ac-accents" role="toolbar" aria-label={t("academy.writing.accents")}>
      <button
        type="button"
        className="ac-key ac-key--wide"
        aria-pressed={upper}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setUpper(!upper)}
      >
        {t("academy.writing.capitals")}
      </button>
      {keys.map((key) => (
        <button
          key={key}
          type="button"
          className="ac-key"
          lang="fr"
          onMouseDown={(e) => e.preventDefault()} // keep the cursor in the text
          onClick={() => onInsert(key)}
        >
          {key}
        </button>
      ))}
    </div>
  );
}

function TaskEditor({
  task,
  text,
  locked,
  saveState,
  onText,
  onSubmit,
}: {
  task: StateTask;
  text: string;
  locked: boolean;
  saveState: SaveState;
  onText: (text: string) => void;
  onSubmit: () => void;
}) {
  const { t, i18n } = useTranslation();
  const area = useRef<HTMLTextAreaElement>(null);
  const words = countWords(text);
  const limits = task.limits.words;
  const status = wordStatus(words, limits);
  const label = localized(task.limits.label, i18n.language);
  const counterId = `words-${task.id}`;

  const insert = (piece: string) => {
    const el = area.current;
    if (!el || locked) return;
    const next = insertAt(text, el.selectionStart, el.selectionEnd, piece);
    onText(next.text);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(next.cursor, next.cursor);
    });
  };

  const ratio = limits ? Math.min(1, words / limits[1]) : 0;
  const minMark = limits ? (limits[0] / limits[1]) * 100 : 0;

  return (
    <div className="ac-task">
      <div className="ac-task__brief">
        <p className="ac-player__eyebrow">
          {t("academy.writing.task", { n: task.number })}
          {label ? ` · ${label}` : ""}
        </p>
        <h2 className="ac-prompt" lang="fr">
          {task.prompt}
        </h2>
        {limits && (
          <p className="ac-footnote">
            {t("academy.writing.limits", { min: limits[0], max: limits[1] })}
            {task.limits.parts
              ? ` · ${Object.entries(task.limits.parts)
                  .map(([part, range]) => t(`academy.writing.parts.${part}`, { min: range[0], max: range[1], defaultValue: `${range[0]}–${range[1]}` }))
                  .join(" · ")}`
              : ""}
          </p>
        )}
      </div>

      {task.documents.length > 0 && (
        <div className="ac-documents" lang="fr">
          {task.documents.map((doc, index) => (
            <article key={index} className="ac-document ac-document--small">
              <p className="ac-document__title">{doc.title || t("academy.writing.document", { n: index + 1 })}</p>
              <p>{doc.text}</p>
            </article>
          ))}
        </div>
      )}

      <div className={`ac-editor${locked ? " ac-editor--locked" : ""}`}>
        <label className="ac-editor__field">
          <span className="visually-hidden">{t("academy.writing.answer", { n: task.number })}</span>
          <textarea
            ref={area}
            lang="fr"
            spellCheck={false}
            autoCorrect="off"
            autoCapitalize="sentences"
            rows={12}
            value={text}
            readOnly={locked}
            aria-describedby={counterId}
            onChange={(e) => onText(e.target.value)}
          />
        </label>
        {!locked && <AccentBar onInsert={insert} />}
        <div className="ac-editor__status">
          <div className={`ac-words ac-words--${status}`}>
            <p id={counterId} className="ac-words__text ao-num" aria-live="polite">
              {limits
                ? t("academy.writing.words", { count: words, min: limits[0], max: limits[1] })
                : t("academy.writing.wordsPlain", { count: words })}
              {status === "short" && ` · ${t("academy.writing.short")}`}
              {status === "long" && ` · ${t("academy.writing.long")}`}
            </p>
            {/* D-163: outside the official range, below as above, the task may fail */}
            {limits && words > 0 && status !== "ok" && status !== "none" && (
              <p className="ac-words__rule ac-footnote">{t(status === "long" ? "conformity.words.over" : "conformity.words.under", { min: limits[0], max: limits[1] })}</p>
            )}
            {limits && (
              <span className="ac-words__meter" aria-hidden="true">
                <span className="ac-words__fill" style={{ transform: `scaleX(${ratio})` }} />
                <span className="ac-words__min" style={{ left: `${minMark}%` }} />
              </span>
            )}
          </div>
          <p className={`ac-save-state ac-save-state--${locked ? "saved" : saveState}`} aria-live="polite">
            <span className="ac-dot" aria-hidden="true" />
            {locked ? t("academy.writing.submitted") : t(`academy.writing.save.${saveState}`)}
          </p>
        </div>
      </div>

      {!locked && (
        <div className="ac-question__actions">
          <Button variant="secondary" icon={<ArrowUpFromLine aria-hidden size={18} />} onClick={onSubmit}>
            {t("academy.writing.submitTask", { n: task.number })}
          </Button>
        </div>
      )}
    </div>
  );
}

export function WritingSection({
  attemptId,
  state,
  onChanged,
}: {
  attemptId: string;
  state: PlayerState;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [texts, setTexts] = useState<Record<string, string>>(() =>
    Object.fromEntries(state.tasks.map((task) => [task.id, task.text])),
  );
  const [saves, setSaves] = useState<Record<string, SaveState>>({});
  const [current, setCurrent] = useState(() => {
    const open = state.tasks.findIndex((task) => task.status !== "submitted" && task.status !== "graded");
    return open < 0 ? 0 : open;
  });
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const timers = useRef(new Map<string, number>());
  const latest = useRef(texts);
  const queue = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    latest.current = texts;
  }, [texts]);

  // Tasks that appear later in the state (never typed here) start from the saved text.
  useEffect(() => {
    setTexts((prev) => {
      const next = { ...prev };
      for (const task of state.tasks) if (!(task.id in next)) next[task.id] = task.text;
      return next;
    });
  }, [state.tasks]);

  const save = useCallback(
    (taskId: string, submit = false): Promise<void> => {
      window.clearTimeout(timers.current.get(taskId));
      timers.current.delete(taskId);
      // One request at a time: the last text always wins.
      queue.current = queue.current.then(async () => {
        setSaves((s) => ({ ...s, [taskId]: "saving" }));
        try {
          await academyAttemptsTextCreate(attemptId, { task: taskId, text: latest.current[taskId] ?? "", submit });
          setSaves((s) => ({ ...s, [taskId]: "saved" }));
        } catch (failure) {
          setSaves((s) => ({ ...s, [taskId]: "error" }));
          if (failure instanceof ApiError && failure.status === 400) await onChanged();
        }
      });
      return queue.current;
    },
    [attemptId, onChanged],
  );

  const flushAll = useCallback(async () => {
    await Promise.all([...timers.current.keys()].map((taskId) => save(taskId)));
  }, [save]);

  // Leaving the tab (phone call, app switch): save at once.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === "hidden") void flushAll();
    };
    document.addEventListener("visibilitychange", onHide);
    const pending = timers.current;
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      // D-171: the section closes at the end of the time: what was just typed is still sent
      [...pending.keys()].forEach((taskId) => void save(taskId));
    };
  }, [flushAll, save]);

  const onText = (taskId: string, text: string) => {
    setTexts((prev) => ({ ...prev, [taskId]: text }));
    latest.current = { ...latest.current, [taskId]: text };
    window.clearTimeout(timers.current.get(taskId));
    timers.current.set(taskId, window.setTimeout(() => void save(taskId), 1500));
  };

  const submitTask = async (taskId: string) => {
    await save(taskId, true);
    await onChanged();
    const next = state.tasks.findIndex((task, i) => i > current && task.status !== "submitted");
    if (next >= 0) setCurrent(next);
  };

  const finish = async () => {
    setFinishing(true);
    try {
      await flushAll();
      await academyAttemptsNextCreate(attemptId);
    } finally {
      await onChanged();
      setFinishing(false);
    }
  };

  const task = state.tasks[current];

  return (
    <section className="ac-writing" aria-label={t("academy.skill.ee")}>
      <div className="ac-task-tabs" role="tablist" aria-label={t("academy.writing.tasks")}>
        {state.tasks.map((item, index) => {
          const sent = item.status === "submitted" || item.status === "graded";
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              className={`ac-task-tab${sent ? " ac-task-tab--sent" : ""}`}
              aria-selected={index === current}
              onClick={() => setCurrent(index)}
            >
              <span className="ac-task-tab__n ao-num" aria-hidden="true">
                {sent ? <Check size={13} strokeWidth={3} /> : item.number}
              </span>
              <span>{t("academy.writing.task", { n: item.number })}</span>
              <span className="ac-task-tab__words ao-num" aria-hidden="true">{t("academy.writing.wordsPlain", { count: countWords(texts[item.id] ?? "") })}</span>
            </button>
          );
        })}
      </div>

      <div className="ac-question__sheet">
        {task ? (
          <TaskEditor
            key={task.id}
            task={task}
            text={texts[task.id] ?? ""}
            locked={task.status !== "" && task.status !== "draft"}
            saveState={saves[task.id] ?? "idle"}
            onText={(text) => onText(task.id, text)}
            onSubmit={() => void submitTask(task.id)}
          />
        ) : (
          <p className="ac-secondary">{t("academy.writing.noTask")}</p>
        )}
      </div>

      <p className="ac-tip">
        <Keyboard aria-hidden size={16} />
        <span>{t("academy.writing.tip")}</span>
      </p>

      <div className="ac-finish">
        {confirmFinish ? (
          <div className="ac-finish__confirm" role="alertdialog" aria-label={t("academy.writing.finish")}>
            <p>{t("academy.writing.finishConfirm")}</p>
            <div className="ac-card__actions">
              <Button variant="ghost" onClick={() => setConfirmFinish(false)}>
                {t("common.cancel")}
              </Button>
              <Button onClick={() => void finish()} disabled={finishing}>
                {finishing ? t("common.loading") : t("academy.writing.finishYes")}
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="ghost" onClick={() => setConfirmFinish(true)}>
            {t("academy.writing.finish")}
          </Button>
        )}
      </div>
    </section>
  );
}
